
import { useQuery } from "@tanstack/react-query";
import { Search, SearchX, X, FilterX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import InsightCard from "@/components/insights/InsightCard";
import { blogTranslations } from "@/utils/translations/blog";
import Footer from "@/components/Footer";
import InsightTagFilter from "@/components/insights/InsightTagFilter";
import { Skeleton } from "@/components/ui/skeleton";
import type { Insight } from "@/types/insight";
import SEOMetaTags from "@/components/SEOMetaTags";
import { useLocation } from "react-router-dom";
import Masonry from "react-masonry-css";

interface InsightsProps {
  language: string;
}

// Masonry breakpoints: 3 columns on desktop, 2 on tablet, 1 on mobile.
// react-masonry-css treats keys as max-widths and picks the smallest match.
const masonryBreakpoints = {
  default: 3,
  1023: 2,
  767: 1,
};

const Insights = ({ language }: InsightsProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const location = useLocation();
  const t = blogTranslations[language as keyof typeof blogTranslations];

  const { data: insights, isLoading } = useQuery({
    queryKey: ["insights"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("insights")
        .select("id, slug, slug_en, slug_ko, title_en, title_ko, description_en, description_ko, external_url, published_at, tags, thumbnail_url, reading_time, views_count, likes_count, status, author")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (error) throw error;
      return data as Insight[];
    },
  });

  const allTags = Array.from(
    new Set(insights?.flatMap((insight) => insight.tags) || [])
  ).sort();

  const filteredInsights = insights?.filter((insight) => {
    const matchesSearch =
      searchQuery === "" ||
      insight.title_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      insight.description_en.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.every((tag) => insight.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  // Render in batches so the browser doesn't request every thumbnail at once.
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, selectedTags]);

  const visibleInsights = filteredInsights?.slice(0, visibleCount);
  const hasMore = (filteredInsights?.length ?? 0) > visibleCount;

  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => c + PAGE_SIZE);
        }
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, visibleInsights?.length]);

  const handleToggleTag = (tag: string) => {
    if (tag === 'all') {
      setSelectedTags([]);
      return;
    }

    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const hasActiveFilters = searchQuery.trim() !== "" || selectedTags.length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedTags([]);
  };

  const renderSkeletons = () => (
    <>
      {/* Card skeletons — match InsightCard layout exactly */}
      <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col rounded-lg border border-gray-100 bg-white overflow-hidden shadow-sm">
            {/* Thumbnail */}
            <Skeleton className="aspect-[3/2] w-full rounded-none bg-gray-200" />
            <div className="flex flex-col flex-grow p-4">
              <div className="flex-grow space-y-2">
                {/* Title */}
                <Skeleton className="h-5 w-full bg-gray-200" />
                <Skeleton className="h-5 w-4/5 bg-gray-200" />
                {/* Date */}
                <Skeleton className="h-3 w-1/3 bg-gray-200 mt-1" />
                {/* Description */}
                <Skeleton className="h-4 w-full bg-gray-200 mt-1" />
                <Skeleton className="h-4 w-5/6 bg-gray-200" />
                {/* Tag pills */}
                <div className="flex gap-1.5 flex-wrap pt-1">
                  <Skeleton className="h-5 w-16 rounded-full bg-gray-200" />
                  <Skeleton className="h-5 w-20 rounded-full bg-gray-200" />
                  <Skeleton className="h-5 w-14 rounded-full bg-gray-200" />
                </div>
              </div>
              {/* Footer */}
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
                <div className="flex gap-1.5">
                  <Skeleton className="h-5 w-14 rounded-full bg-gray-200" />
                  <Skeleton className="h-5 w-12 rounded-full bg-gray-200" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-14 rounded bg-gray-200" />
                  <Skeleton className="h-7 w-7 rounded bg-gray-200" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      <SEOMetaTags
        language={language}
        path={location.pathname}
        pageType="blog"
      />
      <div className="min-h-screen bg-white flex flex-col">
        {/* Hero Banner Section */}
        <div className="relative bg-gray-100 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/render/image/public/dcl_website_asset/banners/insights_banner.webp?width=1920&quality=80&format=webp"
            alt="Insights Banner"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1F2C] mb-6 animate-fade-up [animation-delay:200ms]">
              {t.title}
            </h1>
            <p className="text-xl text-[#403E43] max-w-2xl mx-auto mb-8 animate-fade-up [animation-delay:400ms]">
              {t.subtitle}
            </p>
            <div className="max-w-xl mx-auto animate-fade-up [animation-delay:600ms]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8E9196] h-5 w-5" />
                <Input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 bg-white border-white/60 text-[#333333] placeholder:text-[#8A898C] focus:border-primary/40 focus:ring-primary/20"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#8E9196] transition-colors hover:text-[#333333]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Tag filter badges — in header so mobile users reach content immediately */}
            {!isLoading && allTags.length > 0 && (
              <div className="max-w-2xl mx-auto">
                <InsightTagFilter
                  tags={allTags}
                  selectedTags={selectedTags}
                  onToggleTag={handleToggleTag}
                  language={language}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {isLoading ? (
          renderSkeletons()
        ) : filteredInsights && filteredInsights.length > 0 ? (
          <>
            {/* Result count + clear-filters affordance */}
            <div className="mb-6 flex items-center justify-between gap-4 max-w-6xl mx-auto">
              <p className="text-sm text-gray-500">
                {language === "KO"
                  ? `${filteredInsights.length}개의 아티클`
                  : `Showing ${filteredInsights.length} ${
                      filteredInsights.length === 1 ? "article" : "articles"
                    }`}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-primary"
                >
                  <FilterX className="h-4 w-4" />
                  {t.clearFilters}
                </button>
              )}
            </div>

            <Masonry
              breakpointCols={masonryBreakpoints}
              className="flex gap-8 max-w-6xl mx-auto"
              columnClassName="flex flex-col gap-8"
            >
              {visibleInsights?.map((insight, index) => (
                <div
                  key={insight.id}
                  className="motion-safe:animate-fade-up"
                  style={{ animationDelay: `${(index % PAGE_SIZE) * 60}ms` }}
                >
                  <InsightCard
                    insight={insight}
                    language={language}
                    priority={index < 4}
                  />
                </div>
              ))}
            </Masonry>

            {hasMore && (
              <div ref={sentinelRef} className="h-10" aria-hidden="true" />
            )}
          </>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <SearchX className="mb-4 h-12 w-12 text-gray-300" />
            <p className="mb-6 max-w-sm text-gray-500">{t.noResults}</p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-primary/40 hover:text-primary"
              >
                <FilterX className="h-4 w-4" />
                {t.clearFilters}
              </button>
            )}
          </div>
        )}
      </div>

      <Footer language={language} />
      </div>
    </>
  );
};

export default Insights;
